import { memo } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { type StreamMessage } from "../../hooks/useStreamingText";
import { Text } from "../Text";
import { type ChatTool } from "./ChatTool";

import { ActivityLineRow, resolveActionLine } from "./ActivityLineRow";
import { KickoffRow, resolveKickoff } from "./KickoffRow";
import { MessageRow } from "./MessageRow";
import { ToolGroupRow } from "./ToolGroupRow";
import { DateBadge, DateMarker } from "./transcriptStyles";
import { UserActionGroupRow } from "./UserActionGroupRow";

function formatDateLabel(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const entryDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (entryDay.getTime() === today.getTime()) return "Today";
  if (entryDay.getTime() === yesterday.getTime()) return "Yesterday";

  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  };
  return date.toLocaleDateString("en-US", options);
}

function getGroupTimestamp(grouped: GroupedEntry): number {
  if (grouped.type === "single") return grouped.entry.createdAt;
  if (grouped.type === "userActionGroup")
    return grouped.entries[0]?.createdAt ?? 0;
  if (grouped.entries[0]) return grouped.entries[0].createdAt;
  if (grouped.reasoning) return grouped.reasoning.createdAt;
  return 0;
}

function getDateKey(timestamp: number): string {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export type GroupedEntry =
  | { type: "single"; entry: ChatEntryWithSubTables }
  | {
      type: "userActionGroup";
      entries: ChatEntryWithSubTables[];
      groupId: string;
    }
  | {
      type: "toolGroup";
      entries: ChatEntryWithSubTables[];
      groupId: string;
      reasoning?: ChatEntryWithSubTables;
      isTrailing?: boolean; // true if this is the active group being streamed
    };

type TranscriptProps = {
  groupedEntries: GroupedEntry[];
  isGroupChat: boolean;
  currentUserId: string | undefined;
  expandedToolGroups: Set<string>;
  setExpandedToolGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  expandedUserActionGroups: Set<string>;
  setExpandedUserActionGroups: React.Dispatch<
    React.SetStateAction<Set<string>>
  >;
  expandedToolUse: string | null;
  setExpandedToolUse: React.Dispatch<React.SetStateAction<string | null>>;
  toolsByName: Map<string, ChatTool>;
  streamingEntries: ReadonlyMap<string, StreamMessage>;
  isCurrentUserChat: boolean;
  canWrite: boolean;
  onProvideToolResult: (
    entryId: string,
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean }
  ) => void;
  onRetryFromError: (errorEntryId: string) => void;
  onTypewriterAnimatingChange: (animating: boolean) => void;
  resolveFileHref?: (href: string) => string | null;
};

export const Transcript = memo(function Transcript({
  groupedEntries,
  isGroupChat,
  currentUserId,
  expandedToolGroups,
  setExpandedToolGroups,
  expandedUserActionGroups,
  setExpandedUserActionGroups,
  expandedToolUse,
  setExpandedToolUse,
  toolsByName,
  streamingEntries,
  isCurrentUserChat,
  canWrite,
  onProvideToolResult,
  onRetryFromError,
  onTypewriterAnimatingChange,
  resolveFileHref,
}: TranscriptProps) {
  "use memo";
  return groupedEntries.map((grouped, index) => {
    const nextGrouped = groupedEntries[index + 1];
    const prevGrouped = groupedEntries[index - 1];

    const currentTimestamp = getGroupTimestamp(grouped);
    const prevTimestamp = prevGrouped ? getGroupTimestamp(prevGrouped) : null;
    const showDateMarker =
      currentTimestamp > 0 &&
      (prevTimestamp === null ||
        getDateKey(currentTimestamp) !== getDateKey(prevTimestamp));
    const dateMarker = showDateMarker ? (
      <DateMarker key={`date-${getDateKey(currentTimestamp)}`}>
        <DateBadge>
          <Text variant="text-small" color="var(--text-secondary)">
            {formatDateLabel(currentTimestamp)}
          </Text>
        </DateBadge>
      </DateMarker>
    ) : null;

    if (grouped.type === "userActionGroup") {
      return (
        <UserActionGroupRow
          key={grouped.groupId}
          grouped={grouped}
          dateMarker={dateMarker}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
          expandedUserActionGroups={expandedUserActionGroups}
          setExpandedUserActionGroups={setExpandedUserActionGroups}
        />
      );
    }

    if (grouped.type === "toolGroup") {
      return (
        <ToolGroupRow
          key={grouped.groupId}
          grouped={grouped}
          prevGrouped={prevGrouped}
          nextGrouped={nextGrouped}
          dateMarker={dateMarker}
          expandedToolGroups={expandedToolGroups}
          setExpandedToolGroups={setExpandedToolGroups}
          expandedToolUse={expandedToolUse}
          setExpandedToolUse={setExpandedToolUse}
          toolsByName={toolsByName}
          streamingEntries={streamingEntries}
          onProvideToolResult={onProvideToolResult}
        />
      );
    }

    const entry = grouped.entry;

    const kickoff = resolveKickoff(entry);
    if (kickoff) {
      return (
        <KickoffRow
          key={entry.id}
          kickoff={kickoff}
          dateMarker={dateMarker}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    const actionLine = resolveActionLine(entry);
    if (actionLine) {
      return (
        <ActivityLineRow
          key={entry.id}
          actionLine={actionLine}
          dateMarker={dateMarker}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    return (
      <MessageRow
        key={entry.id}
        entry={entry}
        nextGrouped={nextGrouped}
        dateMarker={dateMarker}
        isGroupChat={isGroupChat}
        currentUserId={currentUserId}
        expandedToolUse={expandedToolUse}
        setExpandedToolUse={setExpandedToolUse}
        toolsByName={toolsByName}
        streamingEntries={streamingEntries}
        isCurrentUserChat={isCurrentUserChat}
        canWrite={canWrite}
        onProvideToolResult={onProvideToolResult}
        onRetryFromError={onRetryFromError}
        onTypewriterAnimatingChange={onTypewriterAnimatingChange}
        resolveFileHref={resolveFileHref}
      />
    );
  });
});
